/** Rendered link-preview layout variant shared by the feed estimator and LinkPreviewCard. */
export type LinkPreviewVariant = 'none' | 'small' | 'tall' | 'wide';

/** Hero asset category used by LinkPreviewCard to choose image fit and variant rules. */
export type LinkPreviewHeroKind = 'logo' | 'main';

/** Normalized hero asset selected from a link-preview payload. */
export interface LinkPreviewHeroSource {
  readonly height?: number;
  readonly kind: LinkPreviewHeroKind;
  readonly url: string;
  readonly width?: number;
}

/** Link-preview fields accepted from Echo wire rows and legacy chat preview rows. */
export interface LinkPreviewGeometryInput {
  readonly description?: string | null;
  readonly image?: {
    readonly height?: number | null;
    readonly url?: string | null;
    readonly width?: number | null;
  } | null;
  readonly image_height?: number | null;
  readonly image_url?: string | null;
  readonly image_width?: number | null;
  readonly imageHeight?: number | null;
  readonly imageUrl?: string | null;
  readonly imageWidth?: number | null;
  readonly logo_height?: number | null;
  readonly logo_url?: string | null;
  readonly logo_width?: number | null;
  readonly logoHeight?: number | null;
  readonly logoUrl?: string | null;
  readonly logoWidth?: number | null;
  readonly publisher?: string | null;
  readonly title?: string | null;
  readonly url?: string | null;
}

/** Matches LinkPreviewCard `max-w-lg`. */
export const LINK_PREVIEW_MAX_WIDTH_PX = 512;

/** Below this natural image size, LinkPreviewCard uses the compact `size-12` variant. */
export const LINK_PREVIEW_SMALL_MAX_DIMENSION_PX = 150;

/** Aspect threshold below which LinkPreviewCard uses the side-media `w-32` tall row. */
export const LINK_PREVIEW_TALL_ASPECT_THRESHOLD = 0.9;

/** Lower bound for LinkPreviewCard's wide `aspectRatio` inline style. */
export const LINK_PREVIEW_ASPECT_RATIO_MIN = 0.5;

/** Upper bound for LinkPreviewCard's wide `aspectRatio` inline style. */
export const LINK_PREVIEW_ASPECT_RATIO_MAX = 3;

/** Fallback for LinkPreviewCard's wide `aspectRatio` when dimensions are missing. */
export const LINK_PREVIEW_FALLBACK_ASPECT_RATIO = 16 / 9;

/** Matches the preview list `pt-1` spacing before the first LinkPreviewCard. */
export const LINK_PREVIEW_LIST_PADDING_TOP_PX = 4;

/** Matches the preview list `gap-1.5` spacing between LinkPreviewCards. */
export const LINK_PREVIEW_LIST_GAP_PX = 6;

/** Matches LinkPreviewCard text containers `px-5`. */
export const LINK_PREVIEW_TEXT_PADDING_X_PX = 20;

/** Matches LinkPreviewCard wide/no-image text container `pt-4`. */
export const LINK_PREVIEW_WIDE_TEXT_PADDING_TOP_PX = 16;

/** Matches LinkPreviewCard wide/no-image text container `pb-6`. */
export const LINK_PREVIEW_WIDE_TEXT_PADDING_BOTTOM_PX = 24;

/** Matches LinkPreviewCard tall text container `py-4`. */
export const LINK_PREVIEW_TALL_TEXT_PADDING_Y_PX = 16;

/** Matches LinkPreviewCard small row `py-4`. */
export const LINK_PREVIEW_SMALL_PADDING_Y_PX = 16;

/** Matches LinkPreviewCard wide/tall text stack `gap-2`. */
export const LINK_PREVIEW_TEXT_PAIR_GAP_PX = 8;

/** Matches LinkPreviewCard small text stack `gap-1`. */
export const LINK_PREVIEW_SMALL_TEXT_GAP_PX = 4;

/** Matches LinkPreviewCard small row media/text spacing `gap-4`. */
export const LINK_PREVIEW_SMALL_CONTENT_GAP_PX = 16;

/** Matches LinkPreviewCard host `text-xs` line box used in measured rows. */
export const LINK_PREVIEW_HOST_LINE_HEIGHT_PX = 16;

/** Matches LinkPreviewCard description `leading-4`. */
export const LINK_PREVIEW_DESCRIPTION_LINE_HEIGHT_PX = 16;

/** Matches LinkPreviewCard description `line-clamp-2`. */
export const LINK_PREVIEW_DESCRIPTION_MAX_LINES = 2;

/** Average glyph width for estimating LinkPreviewCard's clamped description line count. */
export const LINK_PREVIEW_DESCRIPTION_AVG_CHAR_WIDTH_PX = 6.8;

/** Matches LinkPreviewCard tall row `min-h-32`. */
export const LINK_PREVIEW_TALL_MIN_HEIGHT_PX = 128;

/** Matches LinkPreviewCard tall hero column `w-32`. */
export const LINK_PREVIEW_TALL_IMAGE_WIDTH_PX = 128;

/** Matches LinkPreviewCard small logo box `size-12`. */
export const LINK_PREVIEW_SMALL_ICON_SIZE_PX = 48;

/** Selects the image or logo LinkPreviewCard will render as its reserved hero media. */
export function pickLinkPreviewHeroSource(
  preview: LinkPreviewGeometryInput
): LinkPreviewHeroSource | null {
  const imageUrl = firstString(preview.imageUrl, preview.image_url, preview.image?.url);
  if (imageUrl) {
    return {
      height: firstPositiveNumber(preview.imageHeight, preview.image_height, preview.image?.height),
      kind: 'main',
      url: imageUrl,
      width: firstPositiveNumber(preview.imageWidth, preview.image_width, preview.image?.width)
    };
  }

  const logoUrl = firstString(preview.logoUrl, preview.logo_url);
  if (logoUrl) {
    return {
      height: firstPositiveNumber(preview.logoHeight, preview.logo_height),
      kind: 'logo',
      url: logoUrl,
      width: firstPositiveNumber(preview.logoWidth, preview.logo_width)
    };
  }

  return null;
}

/** Classifies a raw preview into the LinkPreviewCard variant used for height estimation. */
export function classifyLinkPreviewVariant(preview: LinkPreviewGeometryInput): LinkPreviewVariant {
  return classifyLinkPreviewSource(pickLinkPreviewHeroSource(preview));
}

/** Classifies a preselected LinkPreviewCard hero source into its rendered variant. */
export function classifyLinkPreviewSource(
  source: LinkPreviewHeroSource | null | undefined
): LinkPreviewVariant {
  if (!source) return 'none';
  if (source.kind === 'logo') return 'small';
  if (!source.width || !source.height) return 'wide';
  if (Math.max(source.width, source.height) < LINK_PREVIEW_SMALL_MAX_DIMENSION_PX) return 'small';
  if (source.width / source.height < LINK_PREVIEW_TALL_ASPECT_THRESHOLD) return 'tall';
  return 'wide';
}

/** Returns the bounded aspect ratio applied to LinkPreviewCard's wide hero box. */
export function clampLinkPreviewAspectRatio(
  width: number | null | undefined,
  height: number | null | undefined
): number {
  const safeWidth = positiveNumber(width);
  const safeHeight = positiveNumber(height);
  if (!safeWidth || !safeHeight) return LINK_PREVIEW_FALLBACK_ASPECT_RATIO;

  const ratio = safeWidth / safeHeight;
  return Math.min(
    LINK_PREVIEW_ASPECT_RATIO_MAX,
    Math.max(LINK_PREVIEW_ASPECT_RATIO_MIN, ratio)
  );
}

/** Estimates the first-paint LinkPreviewCard height for virtualized feed geometry. */
export function estimateLinkPreviewHeight(
  preview: LinkPreviewGeometryInput,
  contentWidth: number
): number {
  assertFinitePositive(contentWidth, 'contentWidth');
  if (!firstString(preview.url)) return 0;

  const cardWidth = Math.min(contentWidth, LINK_PREVIEW_MAX_WIDTH_PX);
  const heroSource = pickLinkPreviewHeroSource(preview);
  const variant = classifyLinkPreviewSource(heroSource);

  if (variant === 'wide' && heroSource) {
    return estimateWideLinkPreviewHeight(preview, heroSource, cardWidth);
  }

  if (variant === 'tall') {
    return estimateTallLinkPreviewHeight(preview, cardWidth);
  }

  if (variant === 'small') {
    return estimateSmallLinkPreviewHeight(preview, cardWidth);
  }

  return estimateWideTextBlockHeight(preview, cardWidth);
}

function estimateWideLinkPreviewHeight(
  preview: LinkPreviewGeometryInput,
  heroSource: LinkPreviewHeroSource,
  cardWidth: number
): number {
  return cardWidth / clampLinkPreviewAspectRatio(heroSource.width, heroSource.height) +
    estimateWideTextBlockHeight(preview, cardWidth);
}

function estimateWideTextBlockHeight(
  preview: LinkPreviewGeometryInput,
  cardWidth: number
): number {
  return LINK_PREVIEW_WIDE_TEXT_PADDING_TOP_PX +
    estimateTextStackHeight(preview, cardWidth - 2 * LINK_PREVIEW_TEXT_PADDING_X_PX, LINK_PREVIEW_TEXT_PAIR_GAP_PX) +
    LINK_PREVIEW_WIDE_TEXT_PADDING_BOTTOM_PX;
}

function estimateTallLinkPreviewHeight(
  preview: LinkPreviewGeometryInput,
  cardWidth: number
): number {
  const textWidth = cardWidth -
    LINK_PREVIEW_TALL_IMAGE_WIDTH_PX -
    2 * LINK_PREVIEW_TEXT_PADDING_X_PX;
  const textHeight = 2 * LINK_PREVIEW_TALL_TEXT_PADDING_Y_PX +
    estimateTextStackHeight(preview, textWidth, LINK_PREVIEW_TEXT_PAIR_GAP_PX);

  return Math.max(LINK_PREVIEW_TALL_MIN_HEIGHT_PX, textHeight);
}

function estimateSmallLinkPreviewHeight(
  preview: LinkPreviewGeometryInput,
  cardWidth: number
): number {
  const textWidth = cardWidth -
    2 * LINK_PREVIEW_TEXT_PADDING_X_PX -
    LINK_PREVIEW_SMALL_ICON_SIZE_PX -
    LINK_PREVIEW_SMALL_CONTENT_GAP_PX;
  const textHeight = estimateTextStackHeight(preview, textWidth, LINK_PREVIEW_SMALL_TEXT_GAP_PX);

  return 2 * LINK_PREVIEW_SMALL_PADDING_Y_PX +
    Math.max(LINK_PREVIEW_SMALL_ICON_SIZE_PX, textHeight);
}

function estimateTextStackHeight(
  preview: LinkPreviewGeometryInput,
  textWidth: number,
  gapPx: number
): number {
  const host = linkPreviewHost(preview);
  const description = linkPreviewDescription(preview, host);
  const hostHeight = host ? LINK_PREVIEW_HOST_LINE_HEIGHT_PX : 0;
  const descriptionHeight = description
    ? estimateDescriptionLineCount(description, textWidth) * LINK_PREVIEW_DESCRIPTION_LINE_HEIGHT_PX
    : 0;
  const gap = host && description ? gapPx : 0;

  return hostHeight + gap + descriptionHeight;
}

function estimateDescriptionLineCount(text: string, textWidth: number): number {
  const safeTextWidth = Math.max(1, textWidth);
  const lineCount = Math.ceil((text.length * LINK_PREVIEW_DESCRIPTION_AVG_CHAR_WIDTH_PX) / safeTextWidth);
  return Math.min(LINK_PREVIEW_DESCRIPTION_MAX_LINES, Math.max(1, lineCount));
}

function linkPreviewHost(preview: LinkPreviewGeometryInput): string | null {
  return firstString(preview.publisher) ?? safeHostname(firstString(preview.url));
}

function linkPreviewDescription(
  preview: LinkPreviewGeometryInput,
  host: string | null
): string | null {
  return firstString(preview.description, preview.title) ?? host ?? firstString(preview.url);
}

function safeHostname(url: string | null | undefined): string | null {
  if (!url) return null;

  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

function firstString(...values: readonly (string | null | undefined)[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.length > 0) return value;
  }

  return null;
}

function firstPositiveNumber(...values: readonly (number | null | undefined)[]): number | undefined {
  for (const value of values) {
    const number = positiveNumber(value);
    if (number !== undefined) return number;
  }

  return undefined;
}

function positiveNumber(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

function assertFinitePositive(value: number, name: string) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${name} must be a finite positive number`);
  }
}
